import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth, notifications, tokens } from '../utils/api';
import en from '../locales/en.json';
import bn from '../locales/bn.json';

const AppContext = createContext(null);
const translations = { en, bn };

export function AppProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [token,   setToken]   = useState(() => localStorage.getItem('kb_token'));
  const [theme,   setTheme]   = useState(() => localStorage.getItem('kb_theme') || 'light');
  const [lang,    setLang]    = useState(() => localStorage.getItem('kb_lang')  || 'en');
  const [loading, setLoading] = useState(!!localStorage.getItem('kb_token'));
  const [unread,  setUnread]  = useState(0);
  const [walletBalance, setWalletBalance] = useState(null);

  const fetchBalance = useCallback(() => {
    if (token) {
      tokens.balance().then(r => setWalletBalance(r.balance)).catch(() => {});
    }
  }, [token]);

  const t = useCallback((key) =>
    translations[lang]?.[key] ?? translations.en[key] ?? key, [lang]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('kb_theme', theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.setAttribute('lang', lang === 'bn' ? 'bn' : 'en');
    localStorage.setItem('kb_lang', lang);
  }, [lang]);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    auth.me()
      .then(data => { setUser(data); fetchUnread(); fetchBalance(); })
      .catch(() => logout())
      .finally(() => setLoading(false));
  }, [token]); // eslint-disable-line

  const fetchUnread = () => {
    notifications.unreadCount().then(r => setUnread(r.count ?? 0)).catch(() => {});
  };

  const login = (newToken, userData) => {
    localStorage.setItem('kb_token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('kb_token');
    setToken(null);
    setUser(null);
    setUnread(0);
    setWalletBalance(null);
  };

  const toggleTheme = () => setTheme(t => t === 'light' ? 'dark' : 'light');
  const toggleLang  = () => setLang(l => l === 'en' ? 'bn' : 'en');

  return (
    <AppContext.Provider value={{
      user, token, theme, lang, loading, unread, walletBalance,
      t, login, logout, toggleTheme, toggleLang, fetchUnread, fetchBalance, setUser,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
